import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControl, InputLabel, Select, MenuItem, TextField, Divider, Box } from '@mui/material';
import type { DocumentFolder } from '../../types';
import { createDocumentFolder } from '../../services/documents';

interface Props {
  open: boolean;
  folders: DocumentFolder[];
  currentFolderId: string | null;
  onClose: () => void;
  onSave: (folderId: string | null, newFolders?: DocumentFolder[]) => Promise<void>;
}

export default function MoveDocumentDialog({ open, folders, currentFolderId, onClose, onSave }: Props) {
  const [folderId, setFolderId] = useState<string>(currentFolderId ?? '');
  const [saving, setSaving] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [localFolders, setLocalFolders] = useState<DocumentFolder[]>(folders);

  useEffect(() => { if (open) { setFolderId(currentFolderId ?? ''); setCreatingFolder(false); setNewFolderName(''); } }, [open, currentFolderId]);
  useEffect(() => { setLocalFolders(folders); }, [folders]);

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    setSaving(true);
    try {
      const created = await createDocumentFolder(name);
      const updated = [...localFolders, created];
      setLocalFolders(updated);
      setFolderId(created.id);
      setCreatingFolder(false);
      setNewFolderName('');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(folderId || null, localFolders); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Move to Folder</DialogTitle>
      <DialogContent>
        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel>Folder</InputLabel>
          <Select value={folderId} label="Folder" onChange={(e) => setFolderId(e.target.value)}>
            <MenuItem value=""><em>None (ungrouped)</em></MenuItem>
            {localFolders.map(f => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}
            <Divider />
            <MenuItem value="__new__" onClick={(e) => { e.stopPropagation(); setCreatingFolder(true); setFolderId(''); }}>
              + New folder…
            </MenuItem>
          </Select>
        </FormControl>
        {creatingFolder && (
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'center' }}>
            <TextField
              size="small"
              label="Folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreateFolder(); }}
              autoFocus
              fullWidth
            />
            <Button size="small" variant="outlined" onClick={handleCreateFolder} disabled={saving || !newFolderName.trim()}>
              Create
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || creatingFolder}>Move</Button>
      </DialogActions>
    </Dialog>
  );
}
